import { useId } from 'react'
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'

/*
 * Controls are plates, not pills: squared, one step proud of the panel they sit
 * in (`bg-elevated`), with a hairline that warms to amber on focus.
 *
 * No `outline-none` here on purpose — the global :focus-visible ring in
 * index.css is the app's single, consistent focus indicator, and the border
 * shift below is only a second, quieter cue.
 *
 * `text-base` under `sm` is not a typographic choice: iOS zooms the viewport on
 * any focused input under 16px, which throws the whole board off screen.
 */
const CONTROL =
  'w-full rounded-[3px] border border-line-strong bg-elevated px-3 text-base text-ink transition placeholder:text-faint focus:border-accent disabled:opacity-60 sm:text-sm'

/** 44px, everywhere, on every device. */
const CONTROL_HEIGHT = 'h-11'

function FieldShell({
  id,
  label,
  hint,
  error,
  required,
  children,
}: {
  id: string
  label: string
  hint?: ReactNode
  error?: string | null
  required?: boolean
  children: ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="signage block text-[10px] text-muted">
        {label}
        {required ? (
          <span className="ml-1 text-accent" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string | null
}

export function TextField({ label, hint, error, className = '', ...rest }: TextFieldProps) {
  const id = useId()
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} required={rest.required}>
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={`${CONTROL} ${CONTROL_HEIGHT} ${error ? 'border-danger' : ''} ${className}`}
        {...rest}
      />
    </FieldShell>
  )
}

export interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string | null
}

export function TextAreaField({
  label,
  hint,
  error,
  className = '',
  rows = 4,
  ...rest
}: TextAreaFieldProps) {
  const id = useId()
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} required={rest.required}>
      <textarea
        id={id}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={`${CONTROL} resize-y py-2.5 ${error ? 'border-danger' : ''} ${className}`}
        {...rest}
      />
    </FieldShell>
  )
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string | null
  children: ReactNode
}

export function SelectField({
  label,
  hint,
  error,
  className = '',
  children,
  ...rest
}: SelectFieldProps) {
  const id = useId()
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} required={rest.required}>
      <select
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={`${CONTROL} ${CONTROL_HEIGHT} ${className}`}
        {...rest}
      >
        {children}
      </select>
    </FieldShell>
  )
}

/** Bare control for table cells and toolbars, where a visible label would be noise. */
export function BareInput({
  label,
  className = '',
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return <input aria-label={label} className={`${CONTROL} ${CONTROL_HEIGHT} ${className}`} {...rest} />
}

export function BareSelect({
  label,
  className = '',
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; children: ReactNode }) {
  return (
    <select
      aria-label={label}
      className={`${CONTROL} ${CONTROL_HEIGHT} ${className}`}
      {...rest}
    >
      {children}
    </select>
  )
}

/**
 * A squared switch — a lever on a plate, not a pill.
 *
 * The 24px track is wrapped in a 44px hit box rather than being grown to touch
 * size, so the control stays visually quiet while still being thumb-safe.
 */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  description?: ReactNode
  disabled?: boolean
}) {
  const id = useId()
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0">
        <label htmlFor={id} className="signage-tight text-sm text-ink">
          {label}
        </label>
        {description ? <p className="mt-0.5 text-sm text-muted">{description}</p> : null}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className="-my-2 -mr-2 grid h-11 w-14 shrink-0 place-items-center rounded-[3px] disabled:opacity-50"
      >
        <span
          aria-hidden="true"
          className={`relative block h-6 w-11 rounded-[3px] border transition-colors ${
            checked ? 'border-accent bg-accent' : 'border-line-strong bg-elevated'
          }`}
        >
          <span
            className={`absolute top-[3px] size-4 rounded-[2px] transition-all ${
              checked ? 'left-[23px] bg-canvas' : 'left-[3px] bg-line-strong'
            }`}
          />
        </span>
      </button>
    </div>
  )
}

export function Checkbox({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: ReactNode
  disabled?: boolean
}) {
  const id = useId()
  return (
    <div className="flex items-center gap-2.5">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="size-4.5 shrink-0 rounded-[2px] accent-[var(--color-accent)]"
      />
      {/* Padding on the label, not the input: it extends the hit target to 44px
          without inflating the box itself. */}
      <label htmlFor={id} className="flex min-h-11 items-center text-sm text-ink">
        {label}
      </label>
    </div>
  )
}
